import { SEARCH_TYPE } from '@/constants/searchType';
import { type ReactNode, useCallback, useRef, useState } from 'react';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import {
  displayPlainText,
  isImgOnlyHtml,
  parseIlr2Anchor,
  parsePlainExternalUrl,
} from '@/utils/stripHtmlForTooltip';
import Link from '@mui/material/Link';
import { Ilr2StructuredImgCell } from '@/components/publicSearch/Ilr2StructuredImgCell';
import { Ilr2MixedHtmlLabel } from '@/components/publicSearch/Ilr2MixedHtmlLabel';
import { HyperLink } from '@/components/publicSearch/resultCell/HyperLink';
import MemberInlineDisplay from '@/components/publicSearch/member/MemberInlineDisplay';
import {
  BooleanValue,
  FieldOption,
  GetAnimalsRow,
  LinkCell,
} from '@/components/publicSearch/constants';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Chip,
  IconButton,
  Portal,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { FilterIcon } from '@/primitives/icons/FilterIcon';
import { FilterPlusIcon } from '@/primitives/icons/FilterPlusIcon';
import VerifiedIcon from '@mui/icons-material/Verified';
import SellOutlined from '@mui/icons-material/SellOutlined';
import { GridRenderCellParams } from '@/primitives/DataGridPro';
import { useQuickFilterActions } from '@/hooks/publicSearch/useQuickFilterActions';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import {
  ANIMAL_STATUS_TONE,
  getAnimalStatusTone,
} from '@/components/animals/animalStatus';
import { ANIMAL_SEX } from '@/components/animals/animalSex';
import { SocietyFormattedDate } from '@/components/publicSearch/SocietyFormattedDate';
import { blue, green, pink, slate, white } from '@/lib/colours';
import { formatAllCapsLabel } from '@/utils/text';
import { MobileCardLinkRow } from '@/hooks/publicSearch/sharedHelpers';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';

function paletteAlpha(
  channel: string | undefined,
  color: string,
  opacity: number,
) {
  return channel ? `rgba(${channel} / ${opacity})` : alpha(color, opacity);
}

export function AnimalSexBadge({ sex }: { sex: string }) {
  const theme = useTheme();
  const { isDarkMode } = useResolvedColorScheme();
  const normalized = sex.trim().toUpperCase();
  const baseSx = {
    width: 28,
    height: 28,
    borderRadius: '999px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as const;

  if (normalized === ANIMAL_SEX.FEMALE) {
    return (
      <Box
        component="span"
        sx={{
          ...baseSx,
          backgroundColor: alpha(pink[500], isDarkMode ? 0.55 : 0.2),
          color: isDarkMode ? pink[300] : pink[500],
        }}
      >
        <FemaleIcon sx={{ fontSize: 18 }} />
      </Box>
    );
  }

  if (normalized === ANIMAL_SEX.MALE) {
    return (
      <Box
        component="span"
        sx={{
          ...baseSx,
          backgroundColor: isDarkMode
            ? alpha(blue[400], 0.45)
            : alpha(blue[600], 0.2),
          color: isDarkMode ? blue[400] : blue[600],
        }}
      >
        <MaleIcon sx={{ fontSize: 18 }} />
      </Box>
    );
  }

  if (
    normalized === ANIMAL_SEX.STEER ||
    normalized === ANIMAL_SEX.WETHER ||
    normalized === ANIMAL_SEX.GELDING
  ) {
    return (
      <Box
        component="span"
        sx={{
          ...baseSx,
          backgroundColor: isDarkMode
            ? alpha(blue[400], 0.45)
            : alpha(blue[600], 0.2),
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            backgroundColor: isDarkMode ? white[500] : blue[600],
            maskImage: 'url(/neuter-sign.png)',
            maskSize: '100% 100%',
            WebkitMaskImage: 'url(/neuter-sign.png)',
            WebkitMaskSize: '100% 100%',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={{
        ...baseSx,
        backgroundColor: isDarkMode
          ? alpha(slate[400], 0.25)
          : alpha(theme.palette.text.secondary, 0.12),
        color: isDarkMode ? slate[300] : theme.palette.text.secondary,
      }}
    >
      <QuestionMarkIcon sx={{ fontSize: 16 }} />
    </Box>
  );
}

/**
 * Renders the animal name cell in the search data grid.
 * On desktop, it renders a HyperLink; on mobile it expands to a full-width
 * card row with a sex badge, name, and a subtitle showing ident + status.
 * The card handles its own navigation via a `<Link>` and stops the data-grid
 * row-click event from firing a second navigation.
 */
export function AnimalListRow({
  orgCode,
  row,
}: {
  orgCode: string;
  row: GetAnimalsRow;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const id = row.ID;
  const rawName = row.ANIMAL_NAME;
  const name = displayPlainText(rawName);
  const identifier = row.ANIMAL_IDENT ?? '';
  const sex = row.SEX ?? '';
  const registrationStatus = row.REGISTRATION_STATUS ?? '';
  const animalStatus = row.ANIMAL_STATUS ?? '';

  if (!isMobile) {
    if (!id) return null;
    const path = `/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${id}`;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Ilr2MixedHtmlLabel raw={rawName} societyId={orgCode} nowrap>
          {(plain) => (
            <HyperLink label={plain || identifier || '--'} pathname={path} />
          )}
        </Ilr2MixedHtmlLabel>
      </Box>
    );
  }

  if (!id) return null;

  const path = `/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${id}`;
  const suffixParts = [
    identifier,
    registrationStatus,
    formatAllCapsLabel(animalStatus),
  ]
    .filter((part) => part && String(part).trim())
    .map((part) => String(part).trim());
  const suffixLabel = suffixParts.join(' · ');
  const displayName = name || identifier || 'Animal';

  return (
    <MobileCardLinkRow href={path}>
      <AnimalSexBadge sex={sex} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="div"
          sx={{
            color: 'info.main',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Ilr2MixedHtmlLabel raw={rawName} societyId={orgCode} nowrap>
            {(plain) => <>{plain || displayName}</>}
          </Ilr2MixedHtmlLabel>
        </Typography>
        <Typography
          component="div"
          sx={{
            color: 'text.secondary',
            fontSize: '12px',
            lineHeight: 1.35,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {suffixLabel}
        </Typography>
      </Box>
    </MobileCardLinkRow>
  );
}

/** Pill used for animal / registration status in Animal Enquiry grid (not MUI Chip). */
function StatusBadge({
  isActive,
  textTransform = 'capitalize',
  children,
}: {
  isActive: boolean;
  textTransform?: 'none' | 'capitalize';
  children: ReactNode;
}) {
  const { isDarkMode } = useResolvedColorScheme();
  return (
    <Box
      sx={(theme) => {
        let backgroundColor: string;
        let color: string;
        if (isActive) {
          backgroundColor = isDarkMode
            ? alpha(green[500], 0.28)
            : paletteAlpha(
                theme.vars?.palette.success.mainChannel,
                theme.palette.success.main,
                0.16,
              );
          color = isDarkMode
            ? green[200]
            : (theme.vars?.palette.success.dark ?? theme.palette.success.dark);
        } else {
          backgroundColor = isDarkMode
            ? alpha(slate[400], 0.35)
            : alpha(slate[500], 0.18);
          color = isDarkMode
            ? slate[100]
            : (theme.vars?.palette.text.secondary ??
              theme.palette.text.secondary);
        }
        return {
          marginTop: '4px',
          marginBottom: '4px',
          borderRadius: '6px',
          padding: '2px 6px',
          minHeight: '20px',
          height: 'fit-content',
          width: 'fit-content',
          fontSize: '11px',
          letterSpacing: '0.10px',
          lineHeight: '16px',
          textTransform,
          backgroundColor,
          color,
        };
      }}
    >
      {children}
    </Box>
  );
}

function GridQuickFilterCell({
  field,
  filterValue,
  children,
  centered = false,
}: {
  field: string;
  filterValue: string;
  children: ReactNode;
  centered?: boolean;
}) {
  const searchType = useSearchType();
  const { applyQuickFilter, upsertFilter } = useQuickFilterActions(
    SEARCH_TYPE.ANIMAL,
  );
  const isAnimalSearch = searchType === SEARCH_TYPE.ANIMAL;

  const contentRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<boolean>(false);
  const [chipPos, setChipPos] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const showChip = useCallback(() => {
    const el = contentRef.current?.firstElementChild as HTMLElement | null;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setChipPos({
      top: rect.top - 2,
      left: rect.right + 4,
    });
    setHovered(true);
  }, []);

  const hideChip = useCallback(() => {
    setHovered(false);
  }, []);

  const handleQuickFilter = () => applyQuickFilter(field, filterValue);
  const handleAddFilter = () => upsertFilter(field, filterValue);

  if (!isAnimalSearch) {
    return <>{children}</>;
  }

  return (
    <Box
      onMouseEnter={showChip}
      onMouseLeave={hideChip}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: centered ? 'center' : undefined,
        width: '100%',
        height: '100%',
        minWidth: 0,
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        {children}
      </Box>
      {hovered && chipPos && (
        <Portal>
          <Chip
            size="small"
            onMouseEnter={showChip}
            onMouseLeave={hideChip}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Tooltip title="Set filter" placement="top">
                  <IconButton
                    size="small"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleQuickFilter();
                    }}
                    sx={{ p: 0.25, color: 'text.primary' }}
                    aria-label="Set filter"
                  >
                    <FilterIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Add filter" placement="top">
                  <IconButton
                    size="small"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddFilter();
                    }}
                    sx={{ p: 0.25, color: 'text.primary' }}
                    aria-label="Add filter"
                  >
                    <FilterPlusIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            }
            sx={{
              position: 'fixed',
              top: chipPos.top,
              left: chipPos.left,
              zIndex: 1300,
              height: 26,
              backgroundColor: 'background.paper',
              boxShadow: 3,
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        </Portal>
      )}
    </Box>
  );
}

export function createRenderCell(field: keyof GetAnimalsRow, orgCode: string) {
  const renderParentAnimalLink = (
    rawLabel: string,
    linkId: number | string,
    options?: {
      filterField?: keyof GetAnimalsRow;
      filterValue?: string;
      fallbackLabel?: string;
    },
  ) => {
    const pathname = `/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${linkId}`;
    const link = (
      <Ilr2MixedHtmlLabel raw={rawLabel} societyId={orgCode} nowrap>
        {(plain) => (
          <HyperLink
            label={plain || options?.fallbackLabel || String(linkId)}
            pathname={pathname}
          />
        )}
      </Ilr2MixedHtmlLabel>
    );
    if (options?.filterField) {
      return (
        <GridQuickFilterCell
          field={options.filterField}
          filterValue={options.filterValue ?? rawLabel}
        >
          {link}
        </GridQuickFilterCell>
      );
    }
    return link;
  };

  return function renderCell(params: GridRenderCellParams<GetAnimalsRow>) {
    const id = params.row.ID;

    if (field === FieldOption.saleCount.value) {
      if (params.row.ID === -1) return '';
      const rawSale = (params.row as Record<string, unknown>)['SALE_COUNT'];
      const n = typeof rawSale === 'number' ? rawSale : Number(rawSale ?? 0);
      const onSale = Number.isFinite(n) && n > 0;
      return (
        <Tooltip
          title={
            onSale
              ? `${n} active listing${n === 1 ? '' : 's'}`
              : 'Not listed for sale'
          }
        >
          <SellOutlined
            fontSize="small"
            sx={{
              color: onSale ? 'success.main' : 'action.disabled',
            }}
          />
        </Tooltip>
      );
    }

    const value = params.row[field] as string;
    const linkId = (params.row[field] as LinkCell)?.id || '';
    const linkValue = (params.row[field] as LinkCell)?.value || '';

    if (!value) return '';
    if (value === BooleanValue.False) return '';
    if (value === BooleanValue.True) {
      return (
        <IconButton color="success">
          <VerifiedIcon />
        </IconButton>
      );
    }

    switch (field) {
      case FieldOption.sex.value: {
        return (
          <GridQuickFilterCell field={field} filterValue={value}>
            <AnimalSexBadge sex={value} />
          </GridQuickFilterCell>
        );
      }
      case FieldOption.animalName.value: {
        return <AnimalListRow orgCode={orgCode} row={params.row} />;
      }
      case FieldOption.animalIdent.value: {
        if (!id) return null;
        if (!value) return '';
        const pathname = `/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${id}`;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <HyperLink label={value} pathname={pathname} />
          </Box>
        );
      }
      case FieldOption.animalStatus.value: {
        const tone = getAnimalStatusTone(value);
        const isActive = tone === ANIMAL_STATUS_TONE.SUCCESS;
        return (
          <StatusBadge isActive={isActive}>
            {formatAllCapsLabel(value).toLowerCase()}
          </StatusBadge>
        );
      }
      case FieldOption.registrationStatus.value: {
        if (!value) return '';
        const plain = displayPlainText(value);
        if (!plain) return '';
        return (
          <GridQuickFilterCell field={field} filterValue={plain}>
            <StatusBadge isActive textTransform="none">
              {plain}
            </StatusBadge>
          </GridQuickFilterCell>
        );
      }
      case FieldOption.dateOfBirth.value: {
        return <SocietyFormattedDate value={value} asDob gridCell />;
      }
      case FieldOption.breeder.value:
      case FieldOption.currentOwner.value: {
        if (!linkId || !linkValue) return '';
        const detailsAvailFlag =
          field === FieldOption.breeder.value
            ? (params.row.BREEDER as LinkCell | undefined)?.detailsAvailFlag
            : (params.row.CURRENT_OWNER as LinkCell | undefined)
                ?.detailsAvailFlag;
        return (
          <GridQuickFilterCell field={field} filterValue={linkValue}>
            <MemberInlineDisplay
              memberDisplayName={linkValue}
              memberId={linkId}
              orgName={orgCode}
              detailsAvailFlag={detailsAvailFlag}
            />
          </GridQuickFilterCell>
        );
      }

      case FieldOption.dam.value:
      case FieldOption.sire.value: {
        if (!linkId || !linkValue) return null;
        return renderParentAnimalLink(linkValue, linkId, {
          filterField: field,
          filterValue: linkValue,
        });
      }
      case 'SIRE_IDENT': {
        const sireCell = params.row.SIRE as LinkCell | undefined;
        const sireLinkId = sireCell?.id;
        const displayValue = value || (sireCell?.value ?? '');
        if (!sireLinkId || !displayValue) {
          return value ? (
            <Box
              component="span"
              sx={{ display: 'inline-block', py: 0.5, px: 1.25 }}
            >
              {displayPlainText(String(value))}
            </Box>
          ) : (
            ''
          );
        }
        return renderParentAnimalLink(displayValue, sireLinkId, {
          fallbackLabel: String(value || sireLinkId),
        });
      }
      case 'DAM_IDENT': {
        const damCell = params.row.DAM as LinkCell | undefined;
        const damLinkId = damCell?.id;
        const displayValue = value || (damCell?.value ?? '');
        if (!damLinkId || !displayValue) {
          return value ? (
            <Box
              component="span"
              sx={{ display: 'inline-block', py: 0.5, px: 1.25 }}
            >
              {displayPlainText(String(value))}
            </Box>
          ) : (
            ''
          );
        }
        return renderParentAnimalLink(displayValue, damLinkId, {
          fallbackLabel: String(value || damLinkId),
        });
      }
    }

    if (typeof value === 'string' && isImgOnlyHtml(value)) {
      return <Ilr2StructuredImgCell value={value} societyId={orgCode} />;
    }

    if (typeof value === 'string') {
      const societyWebsite: string | null = null;
      const anchor = parseIlr2Anchor(value, societyWebsite);
      if (anchor) {
        return (
          <Link
            href={anchor.href}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontWeight: 500, wordBreak: 'break-word' }}
          >
            {anchor.label}
          </Link>
        );
      }
      const plainUrl = parsePlainExternalUrl(value, societyWebsite);
      if (plainUrl) {
        return (
          <Link
            href={plainUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontWeight: 500, wordBreak: 'break-word' }}
          >
            {plainUrl}
          </Link>
        );
      }
    }

    return displayPlainText(value);
  };
}
